import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc } from '../../api/rpc'
import { Button } from '../ui/Button'
import { Field, Input } from '../ui/Field'
import { useToast } from '../ui/Toast'

/** Social token form, used both for the add tab and for reconnecting a failing connector. */
export function SocialTokenForm({ onDone, reconnect, url }: { onDone(): void; reconnect?: boolean; url?: string | null }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  return (
    <form className="space-y-3" onSubmit={async e => {
      e.preventDefault()
      setBusy(true)
      try {
        await rpc.call('connectors.social.connect', { token })
        setToken('')
        toast(t('connectors.social.connected'), 'success')
        onDone()
      } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
    }}>
      <p className="text-sm text-ink-400">
        {t('connectors.social.intro')}
        {url && <> <a className="text-ginger-600 hover:underline" href={`${url}/settings`} target="_blank" rel="noopener noreferrer">{t('connectors.social.openSettings')}</a></>}
      </p>
      <Field label={t('connectors.social.token')} hint={t('connectors.social.tokenHint')}>
        {id => <Input id={id} type="password" autoComplete="off" required value={token} onChange={e => setToken(e.target.value)} placeholder="msk_…" />}
      </Field>
      <Button type="submit" loading={busy}>{reconnect ? t('connectors.social.reconnect') : t('connectors.social.connect')}</Button>
    </form>
  )
}

/** Figma personal access token form, used both for the add popup and for reconnecting. */
export function FigmaTokenForm({ onDone, reconnect }: { onDone(): void; reconnect?: boolean }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  return (
    <form className="space-y-3" onSubmit={async e => {
      e.preventDefault()
      setBusy(true)
      try {
        await rpc.call('connectors.figma.connect', { token })
        setToken('')
        toast(t('connectors.figma.connected'), 'success')
        onDone()
      } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
    }}>
      <p className="text-sm text-ink-400">
        {t('connectors.figma.intro')}{' '}
        <a className="text-ginger-600 hover:underline" href="https://help.figma.com/hc/en-us/articles/8085703771159-Manage-personal-access-tokens" target="_blank" rel="noopener noreferrer">{t('connectors.figma.howTo')}</a>
      </p>
      <p className="rounded-lg bg-cream-200 px-3 py-2 text-[13px] text-ink-300">{t('connectors.figma.scopes')}</p>
      <Field label={t('connectors.figma.token')} hint={t('connectors.figma.tokenHint')}>
        {id => <Input id={id} type="password" autoComplete="off" required value={token} onChange={e => setToken(e.target.value)} placeholder="figd_…" />}
      </Field>
      <Button type="submit" loading={busy}>{reconnect ? t('connectors.figma.reconnect') : t('connectors.figma.connect')}</Button>
    </form>
  )
}

/** Telegram bot token form (add). */
export function TelegramAddForm({ onDone }: { onDone(): void }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  return (
    <form className="space-y-3" onSubmit={async e => {
      e.preventDefault()
      setBusy(true)
      try { await rpc.call('connectors.telegram.connect', { botToken: token }); setToken(''); onDone() }
      catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
    }}>
      <p className="text-sm text-ink-400">{t('connectors.telegram.intro')}</p>
      <Field label={t('connectors.telegram.token')} hint={t('connectors.telegram.tokenHint')}>
        {id => <Input id={id} type="password" autoComplete="off" required value={token} onChange={e => setToken(e.target.value)} placeholder="123456789:AA…" />}
      </Field>
      <Button type="submit" loading={busy}>{t('connectors.connect')}</Button>
    </form>
  )
}

/** Zimbra mail sign-in (email + password), used both for the add popup and for reconnecting. */
export function ZimbraLoginForm({ onDone, reconnect, email: initialEmail }: { onDone(): void; reconnect?: boolean; email?: string }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [host, setHost] = useState('')
  const [email, setEmail] = useState(initialEmail ?? '')
  const [password, setPassword] = useState('')
  const [fromName, setFromName] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    void rpc.call<{ host: string | null }>('connectors.zimbra.status').then(s => setHost(s.host ?? '')).catch(() => undefined)
  }, [])
  return (
    <form className="space-y-3" onSubmit={async e => {
      e.preventDefault()
      setBusy(true)
      try {
        await rpc.call('connectors.zimbra.connect', { email, password, ...(fromName.trim() ? { fromName: fromName.trim() } : {}) })
        setPassword('')
        toast(t('connectors.zimbra.connected'), 'success')
        onDone()
      } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
    }}>
      <p className="text-sm text-ink-400">{t('connectors.zimbra.intro', { host })}</p>
      <Field label={t('connectors.zimbra.email')}>
        {id => <Input id={id} type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} placeholder={t('connectors.zimbra.emailPlaceholder')} />}
      </Field>
      <Field label={t('connectors.zimbra.password')} hint={t('connectors.zimbra.passwordHint')}>
        {id => <Input id={id} type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />}
      </Field>
      <Field label={t('connectors.zimbra.fromName')} hint={t('connectors.zimbra.fromNameHint')}>
        {id => <Input id={id} value={fromName} onChange={e => setFromName(e.target.value)} />}
      </Field>
      <Button type="submit" loading={busy}>{reconnect ? t('connectors.zimbra.reconnect') : t('connectors.zimbra.connect')}</Button>
    </form>
  )
}

/** Jira sign-in (host + PAT), used both for the add popup and for reconnecting. */
export function JiraConnectForm({ onDone, reconnect, host: initialHost }: { onDone(): void; reconnect?: boolean; host?: string }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [host, setHost] = useState(initialHost ?? '')
  const [pat, setPat] = useState('')
  // Defaults to the server-wide value (Settings → Server, then JIRA_STRICT_SSL); the box is the
  // "allow self-signed" opt-out, so the state is inverted.
  const [strictSsl, setStrictSsl] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void rpc.call<{ defaultHost: string | null; strictSsl: boolean }>('connectors.jira.status')
      .then(s => {
        if (!initialHost && s.defaultHost) setHost(s.defaultHost)
        setStrictSsl(s.strictSsl)
      })
      .catch(() => undefined)
  }, [initialHost])

  return (
    <form className="space-y-3" onSubmit={async e => {
      e.preventDefault()
      setBusy(true)
      try {
        await rpc.call('connectors.jira.connect', {
          host: host.trim(),
          pat: pat.trim(),
          strictSsl
        })
        setPat('')
        toast(t('connectors.jira.connected'), 'success')
        onDone()
      } catch (err) {
        toast((err as Error).message, 'error')
      } finally {
        setBusy(false)
      }
    }}>
      <p className="text-sm text-ink-400">
        {t('connectors.jira.intro')}
      </p>
      <Field label={t('connectors.jira.host')} hint={t('connectors.jira.hostHint')}>
        {id => (
          <Input
            id={id}
            type="text"
            required
            value={host}
            onChange={e => setHost(e.target.value)}
            placeholder="https://jira.example.com"
          />
        )}
      </Field>
      <Field label={t('connectors.jira.pat')} hint={t('connectors.jira.patHint')}>
        {id => (
          <Input
            id={id}
            type="password"
            autoComplete="off"
            required
            value={pat}
            onChange={e => setPat(e.target.value)}
            placeholder="NjQyNzk2…"
          />
        )}
      </Field>
      <label className="flex items-center gap-2 text-xs text-ink-400 cursor-pointer pt-1">
        <input
          type="checkbox"
          checked={!strictSsl}
          onChange={e => setStrictSsl(!e.target.checked)}
          className="rounded border-line"
        />
        <span>{t('connectors.jira.allowSelfSigned')}</span>
      </label>
      <Button type="submit" loading={busy}>
        {reconnect ? t('connectors.jira.reconnect') : t('connectors.jira.connect')}
      </Button>
    </form>
  )
}



/**
 * GitHub connect form (docs/connectors.md §10). When the operator configured a GitHub App the
 * install flow is the primary action and the token form is the fallback; without an app only the
 * token form is shown. The two keys `connectors.github.connected` / `.failed` are also used by the
 * redirect toast in ConnectorsPage.
 */
export function GithubConnectForm({ onDone, reconnect, appSlug }: { onDone(): void; reconnect?: boolean; appSlug?: string | null }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [showToken, setShowToken] = useState(!appSlug)
  const install = async () => {
    try { window.location.assign((await rpc.call<{ url: string }>('connectors.github.appUrl', {})).url) }
    catch (err) { toast((err as Error).message, 'error') }
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-400">
        {t('connectors.github.intro')}{' '}
        <a className="text-ginger-600 hover:underline" href="https://github.com/settings/personal-access-tokens/new" target="_blank" rel="noopener noreferrer">{t('connectors.github.howTo')}</a>
      </p>
      <p className="rounded-lg bg-cream-200 px-3 py-2 text-[13px] text-ink-300">{t('connectors.github.scopes')}</p>
      {appSlug && (
        <div className="space-y-2">
          <Button onClick={() => void install()}>{t('connectors.github.connectWithApp')}</Button>
          {!showToken && (
            <button type="button" className="block text-[13px] text-ginger-600 hover:underline" onClick={() => setShowToken(true)}>
              {t('connectors.github.orToken')}
            </button>
          )}
        </div>
      )}
      {!appSlug && <p className="rounded-lg bg-cream-200 px-3 py-2 text-[13px] text-ink-300">{t('connectors.github.appRequired')}</p>}
      {showToken && (
        <form className="space-y-3" onSubmit={async e => {
          e.preventDefault()
          setBusy(true)
          try {
            await rpc.call('connectors.github.connect', { token })
            setToken('')
            toast(t('connectors.github.connected'), 'success')
            onDone()
          } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
        }}>
          <Field label={t('connectors.github.token')} hint={t('connectors.github.tokenHint')}>
            {id => <Input id={id} type="password" autoComplete="off" required value={token} onChange={e => setToken(e.target.value)} placeholder="ghp_…" />}
          </Field>
          <Button type="submit" loading={busy}>{reconnect ? t('connectors.github.reconnect') : t('connectors.github.connect')}</Button>
        </form>
      )}
    </div>
  )
}
